import {Chip} from "@/components/chip/Chip.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {PillStatus, StatusPillDisplayConfig, StatusPillProps} from "@/components/statusPill/StatusPill.types.ts";
import classNames from "classnames";
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowDown,
  FiArrowUp,
  FiBell,
  FiBellOff,
  FiCheck,
  FiClock,
  FiEye,
  FiInfo,
  FiMinusCircle,
  FiPause,
  FiRefreshCw,
  FiSearch,
  FiTool,
  FiTrash,
  FiTrendingDown,
  FiXCircle,
  FiZap,
} from "react-icons/fi";
import {GoDotFill} from "react-icons/go";
import {IoInformation} from "react-icons/io5";
import styles from "./StatusPill.module.scss";

const StatusPillMap: Record<PillStatus, StatusPillDisplayConfig> = {
  [PillStatus.ACTIVE]: {icon: undefined, color: "blue", displayName: "Active"},
  [PillStatus.ACTIVE_INCIDENTS]: {icon: FiAlertTriangle, color: "orange", displayName: "Active Incidents"},
  [PillStatus.ACTIVE_INTEGRATION]: {icon: GoDotFill, color: "green", displayName: "Active"},
  [PillStatus.ACTIVE_MAINTENANCE]: {icon: FiAlertTriangle, color: "orange", displayName: "Active Maintenance"},
  [PillStatus.CRITICAL]: {icon: FiAlertTriangle, color: "red", displayName: "Critical"},
  [PillStatus.DEGRADED_PERFORMANCE]: {icon: FiTrendingDown, color: "orange", displayName: "Degraded Performance"},
  [PillStatus.DELETED]: {icon: FiTrash, color: "gray", displayName: "Deleted"},
  [PillStatus.DOWN]: {icon: FiArrowDown, color: "red", displayName: "Down"},
  [PillStatus.ENDED]: {icon: FiCheck, color: "gray", displayName: "Ended"},
  [PillStatus.ERROR]: {icon: FiAlertTriangle, color: "red", displayName: "Error"},
  [PillStatus.GENERAL_NOTICE]: {icon: FiInfo, color: "gray", displayName: "General Notice"},
  [PillStatus.IDENTIFIED]: {icon: FiSearch, color: "blue", displayName: "Identified"},
  [PillStatus.IGNORED]: {icon: FiBellOff, color: "blue", displayName: "Ignored"},
  [PillStatus.INVESTIGATING]: {icon: FiEye, color: "orange", displayName: "Investigating"},
  [PillStatus.IN_MAINTENANCE]: {icon: FiTool, color: "yellow", displayName: "In Maintenance"},
  [PillStatus.MAINTENANCE]: {icon: FiTool, color: "yellow", displayName: "Maintenance"},
  [PillStatus.MONITORING]: {icon: FiActivity, color: "yellow", displayName: "Monitoring"},
  [PillStatus.NOTIFICATION]: {icon: IoInformation, color: "gray", displayName: "Notification"},
  [PillStatus.NOT_CONFIGURED]: {icon: undefined, color: "red", displayName: "Not Configured"},
  [PillStatus.NOT_REPORTING]: {icon: FiMinusCircle, color: "gray", displayName: "Not Reporting"},
  [PillStatus.NO_CHANGE]: {icon: FiMinusCircle, color: "gray", displayName: "No Change"},
  [PillStatus.OK]: {icon: FiCheck, color: "green", displayName: "Ok"},
  [PillStatus.ONGOING]: {icon: FiAlertTriangle, color: "red", displayName: "Ongoing"},
  [PillStatus.OPERATIONAL]: {icon: FiCheck, color: "green", displayName: "Operational"},
  [PillStatus.OUTAGE]: {icon: FiAlertTriangle, color: "red", displayName: "Major Outage"},
  [PillStatus.OUTAGE_REPORTED]: {icon: FiArrowDown, color: "red", displayName: "Outage Reported"},
  [PillStatus.PARTIAL_OUTAGE]: {icon: FiAlertTriangle, color: "orange", displayName: "Partial Outage"},
  [PillStatus.PAUSED]: {icon: FiPause, color: "gray", displayName: "Paused"},
  [PillStatus.PENDING]: {icon: undefined, color: "yellow", displayName: "Pending"},
  [PillStatus.READY_TO_DEPLOY]: {icon: FiArrowUp, color: "blue", displayName: "Ready to Deploy"},
  [PillStatus.RESOLVED]: {icon: FiCheck, color: "green", displayName: "Resolved"},
  [PillStatus.SCHEDULED]: {icon: FiClock, color: "gray", displayName: "Scheduled"},
  [PillStatus.SCHEDULED_MAINTENANCE]: {icon: FiClock, color: "yellow", displayName: "Maintenance"},
  [PillStatus.SILENCED]: {icon: FiBellOff, color: "blue", displayName: "Ignored"},
  [PillStatus.UNKNOWN]: {icon: undefined, color: "red", displayName: "Unknown"},
  [PillStatus.UNREACHABLE]: {icon: undefined, color: "red", displayName: "Unreachable"},
  [PillStatus.UP]: {icon: FiArrowUp, color: "green", displayName: "Up"},
  [PillStatus.WARNING]: {icon: FiAlertTriangle, color: "orange", displayName: "Warning"},

  // Placeholder icons/colors pending design input
  [PillStatus.INTEGRATION_EVENT_ALERT_RAISED]: {icon: FiBell, color: "red", displayName: "Alert Raised"},
  [PillStatus.INTEGRATION_EVENT_ALERT_CLEARED]: {icon: FiCheck, color: "green", displayName: "Alert Cleared"},
  [PillStatus.INTEGRATION_EVENT_PERIODIC]: {icon: FiClock, color: "blue", displayName: "Periodic"},
  [PillStatus.INTEGRATION_EVENT_PERIODIC_RAW]: {icon: FiClock, color: "blue", displayName: "Periodic (Raw)"},
  [PillStatus.INTEGRATION_EVENT_RECONFIGURED]: {icon: FiRefreshCw, color: "yellow", displayName: "Reconfigured"},
  [PillStatus.INTEGRATION_EVENT_TIMESERIES]: {icon: FiActivity, color: "blue", displayName: "Timeseries"},
  [PillStatus.INTEGRATION_EVENT_TEST]: {icon: FiZap, color: "gray", displayName: "Test"},

  [PillStatus.MAINTENANCE_EVENT_ENTERED]: {icon: FiTool, color: "yellow", displayName: "Check Maintenance Started"},
  [PillStatus.MAINTENANCE_EVENT_EXITED]: {icon: FiCheck, color: "gray", displayName: "Check Maintenance Ended"},
  [PillStatus.MAINTENANCE_EVENT_CHECK_PAUSED]: {icon: FiPause, color: "gray", displayName: "Check Paused"},
  [PillStatus.MAINTENANCE_EVENT_CHECK_RESUMED]: {icon: FiActivity, color: "green", displayName: "Check Resumed"},
  [PillStatus.MAINTENANCE_EVENT_SCHEDULE_CREATED]: {icon: FiClock, color: "gray", displayName: "Schedule Created"},
  [PillStatus.MAINTENANCE_EVENT_SCHEDULE_UPDATED]: {icon: FiClock, color: "gray", displayName: "Schedule Updated"},
  [PillStatus.MAINTENANCE_EVENT_SCHEDULE_DELETED]: {icon: FiTrash, color: "gray", displayName: "Schedule Deleted"},
  [PillStatus.MAINTENANCE_EVENT_SCHEDULE_PAUSED]: {icon: FiPause, color: "gray", displayName: "Schedule Paused"},
  [PillStatus.MAINTENANCE_EVENT_SCHEDULE_RESUMED]: {icon: FiActivity, color: "green", displayName: "Schedule Resumed"},
  [PillStatus.MAINTENANCE_EVENT_EXCEPTION_ADDED]: {
    icon: FiAlertTriangle,
    color: "orange",
    displayName: "Exception Added",
  },
  [PillStatus.MAINTENANCE_EVENT_OCCURRENCE_STARTED]: {
    icon: FiTool,
    color: "yellow",
    displayName: "Maintenance Started",
  },
  [PillStatus.MAINTENANCE_EVENT_OCCURRENCE_COMPLETED]: {
    icon: FiCheck,
    color: "gray",
    displayName: "Maintenance Ended",
  },
  [PillStatus.MAINTENANCE_EVENT_OCCURRENCE_CANCELED]: {
    icon: FiXCircle,
    color: "gray",
    displayName: "Maintenance Stopped",
  },
  [PillStatus.MAINTENANCE_EVENT_OCCURRENCE_REPLACED]: {
    icon: FiRefreshCw,
    color: "gray",
    displayName: "Maintenance Replaced",
  },
  [PillStatus.MAINTENANCE_EVENT_NOTIFICATION_CREATED]: {
    icon: IoInformation,
    color: "gray",
    displayName: "Notification Created",
  },
  [PillStatus.MAINTENANCE_EVENT_NOTIFICATION_UPDATED]: {
    icon: IoInformation,
    color: "gray",
    displayName: "Notification Updated",
  },
  [PillStatus.MAINTENANCE_EVENT_NOTIFICATION_DELETED]: {
    icon: FiTrash,
    color: "gray",
    displayName: "Notification Deleted",
  },
  [PillStatus.MAINTENANCE_EVENT_NOTIFICATION_SENT]: {
    icon: FiBell,
    color: "gray",
    displayName: "Notification Sent",
  },
};

// It is clear the intention is that based on context only certain statuses are allowed.
// I have left the logic to handle this out of the component to be added later.
// Some statuses may be missing please add!
// TODO this needs to be cleaned up. We don't need 3 types.
//    Right now we should only have 2 types, one with boarder are background and one without. (maybe a marketing?)
export const StatusPill = ({type, status, variant, className, mobileStatus, disabled}: StatusPillProps) => {
  const statusConfig = StatusPillMap[status];
  const colorClass = disabled ? "disabled" : statusConfig.color;
  if (variant !== "text" && !statusConfig.icon) {
    console.warn(`No icon defined for status: ${status}. Using text variant instead.`);
    variant = "text"; // Fallback to text if no icon is available
  }
  const mobileStatusConfig = mobileStatus ? StatusPillMap[mobileStatus] : null;

  if (variant === "icon") {
    return (
      <>
        <Icon
          icon={statusConfig.icon!}
          shape={"circle"}
          className={classNames(className, styles[type], styles[colorClass], styles[variant], {
            [styles.main]: mobileStatusConfig?.icon,
          })}
        />
        {mobileStatusConfig?.icon && (
          <Icon
            icon={mobileStatusConfig.icon}
            shape="circle"
            className={classNames(
              className,
              styles[type],
              styles[mobileStatusConfig.color],
              styles[variant],
              styles.mobile
            )}
          />
        )}
      </>
    );
  } else if (variant === "text") {
    return (
      <>
        <Chip
          className={classNames(className, styles[type], styles[colorClass], styles[variant], {
            [styles.main]: mobileStatusConfig,
          })}
        >
          {statusConfig.displayName}
        </Chip>
        {mobileStatusConfig && (
          <Chip
            className={classNames(
              className,
              styles[type],
              styles[mobileStatusConfig.color],
              styles[variant],
              styles.mobile
            )}
          >
            {mobileStatusConfig.displayName}
          </Chip>
        )}
      </>
    );
  } else if (variant === "textAndIcon") {
    return (
      <>
        <Chip
          className={classNames(className, styles[type], styles[colorClass], styles[variant], styles[variant], {
            [styles.main]: mobileStatusConfig?.icon,
          })}
        >
          <Icon
            icon={statusConfig.icon!}
            removeDivDimensions
            className={classNames(className, styles[colorClass], styles[variant], "me-1")}
          />
          <span className={classNames({[styles.pillText]: !disabled, [styles.pillTextDisabled]: disabled})}>
            {statusConfig.displayName}
          </span>
        </Chip>
        {mobileStatusConfig?.icon && (
          <Chip
            className={classNames(
              className,
              styles[type],
              styles[mobileStatusConfig.color],
              styles[variant],
              styles.mobile
            )}
          >
            <Icon
              icon={mobileStatusConfig.icon}
              removeDivDimensions
              className={classNames(className, styles[mobileStatusConfig.color], styles[variant], "me-1")}
            />
            {mobileStatusConfig.displayName}
          </Chip>
        )}
      </>
    );
  }
};
